class NavItemElement extends HTMLElement {
  connectedCallback() {
    this._link = this.querySelector('a[aria-expanded]');
    this._overlay = this.querySelector('.nav-item__overlay');
    if (!this._link || !this._overlay) return;

    this._hovered = false;
    this._searchOpen = false;
    this._controller = new AbortController();
    const { signal } = this._controller;

    this.addEventListener(
      'mouseenter',
      () => {
        this._hovered = true;
        // While the search has focus it owns the surface — don't hover-open over it.
        if (this._searchOpen) return;
        this._open();
      },
      { signal }
    );

    this.addEventListener(
      'mouseleave',
      () => {
        this._hovered = false;
        this._close();
      },
      { signal }
    );

    this._link.addEventListener('click', (e) => this._handleClick(e), { signal });

    // Links without an href act as buttons, so activate them via keyboard too
    if (this._link.getAttribute('role') === 'button') {
      this._link.addEventListener('keydown', (e) => this._handleKeydown(e), { signal });
    }

    // Close when another nav item opens
    document.addEventListener(
      'chh-nav-item:open',
      (e) => {
        if (e.target !== this) this._close();
      },
      { signal }
    );

    // Track the search panel so hover-open can defer to it
    document.addEventListener(
      'chh-search:open',
      () => {
        this._searchOpen = true;
        this._close();
      },
      { signal }
    );
    document.addEventListener(
      'chh-search:close',
      () => {
        this._searchOpen = false;
      },
      { signal }
    );

    // Close when the header slides out of view on scroll
    document.addEventListener('chh-header:hidden', () => this._close(), { signal });

    // Close on Escape
    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Escape' && !this._overlay.hidden) {
          this._close();
          this._link.focus();
        }
      },
      { signal }
    );

    // Close when clicking outside
    document.addEventListener(
      'click',
      (e) => {
        if (!this.contains(e.target)) this._close();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this._controller?.abort();
  }

  _open() {
    this.dispatchEvent(new CustomEvent('chh-nav-item:open', { bubbles: true }));
    this._link.setAttribute('aria-expanded', 'true');
    this._overlay.hidden = false;
  }

  _close() {
    this._link.setAttribute('aria-expanded', 'false');
    this._overlay.hidden = true;
  }

  _handleClick(e) {
    // Mouse users: hover already opened the menu, so let the click navigate.
    // If it's still closed while hovered, hover-open was suppressed by the
    // search having focus — treat the click as a deliberate open.
    if (this._hovered && !this._overlay.hidden) return;
    e.preventDefault();
    this._overlay.hidden ? this._open() : this._close();
  }

  _handleKeydown(e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    // Prevent Space from scrolling the page
    e.preventDefault();
    this._link.click();
  }
}

customElements.define('chh-nav-item', NavItemElement);

export default NavItemElement;
